<template>
  <div class="cash-call-reconciliation">
    <!-- Header (story 4.1): the eyebrow describes how the TABLE was matched, so
         it belongs only to the table. The empty state is self-describing and
         carries its own heading, and the wizard already labels the step - so
         repeating the title above a "nothing matched" message just states a
         match that did not happen. -->
    <template v-if="!loading && !error && rows.length">
      <h4 class="cash-call-reconciliation__title">Cash Call Reconciliation</h4>
      <p class="cash-call-reconciliation__eyebrow">
        Matched on Claim Reference and LimeIQ Key
      </p>
    </template>

    <!-- LOADING -->
    <div v-if="loading" class="text-center py-5">
      <b-spinner class="cash-call-reconciliation__spinner" />
      <p class="mt-3 mb-0 text-muted">Checking this export for cash calls…</p>
    </div>

    <!-- ERROR -->
    <div v-else-if="error" class="text-center py-5">
      <p class="text-danger mb-3">{{ error }}</p>
      <b-button class="btn-grey" @click="fetchRows">Retry</b-button>
    </div>

    <!-- EMPTY STATE (story 4.6) - a confirmation that nothing matched, not a
         warning: the export is perfectly valid, there is simply nothing to
         reconcile. Centred tick, heading, explanation. -->
    <div v-else-if="!rows.length" class="cash-call-reconciliation__empty">
      <div class="cash-call-reconciliation__empty-check">&#10003;</div>
      <h3>No cash calls matched this report</h3>
      <p>
        No LimeIQ cash calls match the claims in your selected filters on Claim
        Reference and LimeIQ Key. No netting rules will be applied to this export.
      </p>
    </div>

    <!-- RECONCILIATION TABLE (story 4.2) -->
    <div v-else>
      <!-- Filtering lives in the column headings themselves, so the toolbar
           only reports what is currently filtered and offers the download. -->
      <div class="cash-call-reconciliation__toolbar">
        <template v-if="hasActiveFilter">
          <!-- Counted against the actionable rows only, so the denominator
               matches what this count is describing - flagged rows are listed
               separately and carry no action. -->
          <span class="cc-count">
            Showing {{ visibleRows.length }} of {{ actionableRowCount }}
          </span>
          <!-- Each active filter is named with its value, so the reader can see
               exactly what is hiding the other rows - and lift one without
               clearing the rest. -->
          <button
            v-for="filter in activeFilters"
            :key="filter.field"
            type="button"
            class="cc-filter-chip"
            :title="`Remove the ${filter.label} filter`"
            @click="setColumnFilter(filter.field, '')"
          >
            <span class="cc-filter-chip__label">{{ filter.label }}:</span>
            <span class="cc-filter-chip__value">{{ filter.value }}</span>
            <span class="cc-filter-chip__x" aria-hidden="true">&times;</span>
          </button>
          <b-button
            v-if="activeFilterCount > 1"
            size="sm"
            variant="link"
            class="cc-clear-btn"
            @click="clearFilters"
          >
            Clear all
          </b-button>
        </template>
        <b-button
          size="sm"
          class="cc-tool-btn ml-auto"
          :disabled="!visibleRows.length"
          @click="downloadExcel"
        >
          Download
        </b-button>
      </div>

      <div class="cash-call-reconciliation__table-wrap">
      <table class="cash-call-reconciliation__table">
        <!-- Explicit width budget. `table-layout: fixed` would otherwise split
             the width evenly and truncate the headers; the identity columns are
             given only what their content needs so the money columns and the
             two controls keep enough room to render in full. -->
        <colgroup>
          <!-- Identity values may be abbreviated (the full value is on hover);
               dates and every figure must render in full, so the money and date
               columns are budgeted for their widest content. -->
          <!-- Budgeted so each HEADING fits on one line with its filter icon,
               which is wider than the data beneath it in the identity columns
               ("LimeIQ Reference" over "420...") - the heading is what sets the
               minimum here, not the value. -->
          <col style="width: 5%" />  <!--  1 Claim Ref (full) -->
          <col style="width: 5.3%" />    <!--  2 LimeIQ Key (truncates, hover) -->
          <col style="width: 7.5%" />    <!--  3 LimeIQ Reference (truncates, hover) -->
          <col style="width: 6.5%" />  <!--  4 Insured Name (truncates, hover) -->
          <col style="width: 5.7%" />  <!--  5 Coverholder (truncates, hover) -->
          <col style="width: 6.1%" />    <!--  6 Date of Loss (full) -->
          <col style="width: 6.4%" />  <!--  7 Imported: Indemnity (full) -->
          <col style="width: 4.6%" />  <!--  8 Imported: Fees (full) -->
          <!-- Submitted is the date the CASH CALL was raised, so it sits inside
               the LimeIQ Cash Call group rather than among the claim identity
               columns - it is also what distinguishes several cash calls
               raised against the same claim and key. -->
          <col style="width: 5%" />    <!--  9 Cash Call: Submitted (full) -->
          <col style="width: 6.1%" />    <!-- 10 Cash Call: Indemnity (up to 500,000.00) -->
          <col style="width: 4.2%" />    <!-- 11 Cash Call: Fees (full) -->
          <!-- Left-aligned, so surplus width shows as a gap between the state
               word and the right rule - but "OUTSTANDING" (the longest state,
               and the one that keeps money out of the export) must still fit
               beside the switch without escaping the column. -->
          <col style="width: 7.8%" />  <!-- 12 Mark as Paid? (track + "OUTSTANDING") -->
          <!-- Matched to "Mark as Paid?" above: the two are the only editable
               controls in the table and sit side by side, so unequal widths
               read as a mistake rather than a distinction. -->
          <col style="width: 7.8%" />  <!-- 13 Cash Call Action -->
          <!-- The derived columns hold editable inputs when the action is
               "Other", so they are budgeted for a typed figure (e.g. 500000.00),
               not just a rendered one. -->
          <!-- These figures are right-aligned, so any width beyond what the
               widest amount needs shows up as dead space on the LEFT of the
               column, between the band rule and the number. Budgeted close to
               the content rather than generously. -->
          <col style="width: 6.4%" />  <!-- 14 Export Paid: Indemnity -->
          <col style="width: 4.6%" />    <!-- 15 Export Paid: Fees -->
          <col style="width: 6.4%" />  <!-- 16 In Totals: Indemnity -->
          <col style="width: 4.6%" />    <!-- 17 In Totals: Fees -->
        </colgroup>
        <thead>
          <tr class="cash-call-reconciliation__group-row">
            <th colspan="6"></th>
            <th colspan="2" class="group">Imported Paid This Month</th>
            <th colspan="3" class="group">LimeIQ Cash Call</th>
            <th rowspan="2" class="mid mark-col l">
              <ColumnHead label="Mark as Paid?" field="status" :options="statusOptions" />
            </th>
            <th rowspan="2" class="mid l nowrap-head">
              <span class="head-inner">
                <ColumnHead label="Cash Call Action" field="action" :options="actionOptions" />
                <span :id="tooltipId" class="info-icon">i</span>
              </span>
              <!-- A popover, not a tooltip: b-tooltip is capped narrow, which
                   forced these six explanations into a very tall column. -->
              <!-- Opens UPWARD: this card lists six options and is taller than
                   the rows beneath the header, so opening downward ran it off
                   the bottom of the viewport and hid the last explanations. -->
              <b-popover
                :target="tooltipId"
                triggers="hover focus"
                placement="topleft"
                boundary="viewport"
                custom-class="cc-action-help"
                title="Cash Call Action — what each option does"
              >
                <div
                  v-for="opt in actionHelp"
                  :key="opt.label"
                  class="cc-action-help__item"
                >
                  <div class="cc-action-help__label">{{ opt.label }}</div>
                  <div class="cc-action-help__text">{{ opt.help }}</div>
                </div>
              </b-popover>
            </th>
            <th colspan="2" class="group">Export Paid This Month</th>
            <th colspan="2" class="group">Included in Totals</th>
          </tr>
          <tr class="cash-call-reconciliation__sub-row">
            <!-- Every column carries its own search icon: clicking it opens a
                 filter input for that column alone. The icon turns solid while
                 that column is filtering, so an active filter is visible
                 without opening anything. -->
            <th class="whole">
              <ColumnHead label="Claim Ref" field="claim_reference" />
            </th>
            <th><ColumnHead label="LimeIQ Key" field="limeiq_key" /></th>
            <th class="gap-after"><ColumnHead label="LimeIQ Reference" field="limeiq_reference" /></th>
            <th><ColumnHead label="Insured Name" field="insured_name" /></th>
            <th class="coverholder-col"><ColumnHead label="Coverholder" field="coverholder" /></th>
            <th><ColumnHead label="Date of Loss" field="date_of_loss" date /></th>
            <!-- `l` opens each group with a rule; its right edge is closed by
                 the next group's own rule. -->
            <th class="num l"><ColumnHead label="Indemnity" field="imported_indemnity" align="right" /></th>
            <th class="num"><ColumnHead label="Fees" field="imported_fees" align="right" /></th>
            <th class="l"><ColumnHead label="Submitted" field="submitted" date /></th>
            <th class="num"><ColumnHead label="Indemnity" field="cash_call_indemnity" align="right" /></th>
            <th class="num"><ColumnHead label="Fees" field="cash_call_fees" align="right" /></th>
            <th class="num l"><ColumnHead label="Indemnity" field="export_indemnity" align="right" /></th>
            <th class="num"><ColumnHead label="Fees" field="export_fees" align="right" /></th>
            <th class="num l"><ColumnHead label="Indemnity" field="totals_indemnity" align="right" /></th>
            <th class="num"><ColumnHead label="Fees" field="totals_fees" align="right" /></th>
          </tr>
        </thead>
        <tbody>
          <!-- A row actioned HERE renders bold, which is what separates it from
               one that arrived already Paid from the Cash Call page. -->
          <!-- Flagged rows: the cash call's claim reference is in this export
               but under a DIFFERENT LimeIQ key, so there is no exported row to
               reconcile it against. Shown rather than dropped so the TPA can
               see it happened, greyed and without controls because there is no
               decision to make here - the fix is upstream, hence the support
               line. The export's own figures are unaffected. -->
          <tr
            v-for="{ row } in visibleMismatchedRows"
            :key="rowKey(row)"
            class="is-mismatch"
          >
            <td class="ident whole">{{ row.claim_reference }}</td>
            <td class="ident key" :title="row.limeiq_key">{{ row.limeiq_key }}</td>
            <td class="ident gap-after" :title="row.limeiq_reference">{{ row.limeiq_reference || '—' }}</td>
            <td class="ident" :title="row.insured_name">{{ row.insured_name || '—' }}</td>
            <td class="ident coverholder-col" :title="row.coverholder">{{ row.coverholder || '—' }}</td>
            <td class="date">{{ formatDate(row.date_of_loss) }}</td>
            <!-- One cell across every figure and control column: there are no
                 amounts to show and nothing to action. -->
            <!-- Names the field that actually disagreed. These rows are found
                 BY claim reference, so the claim reference is what matched and
                 the key is what differs - the earlier wording said the reverse
                 and sent anyone investigating to the wrong column. -->
            <td class="mismatch-note" colspan="11">
              LimeIQ key does not match the exported key for this claim — please contact
              <a href="mailto:support@limeiq.com">support@limeiq.com</a>
            </td>
          </tr>

          <tr
            v-for="{ row, index } in visibleRows"
            :key="rowKey(row)"
            :class="{ 'is-actioned-here': isActionedHere(row) }"
          >
            <!-- Claim Ref shows in full - it is half of what the match is made
                 on, and a partial claim number is meaningless. Every other
                 identity value is abbreviated, carrying its full value as a
                 hover tooltip. -->
            <td class="ident whole">{{ row.claim_reference }}</td>
            <td class="ident key" :title="row.limeiq_key">{{ row.limeiq_key }}</td>
            <td class="ident gap-after" :title="row.limeiq_reference">{{ row.limeiq_reference || '—' }}</td>
            <td class="ident" :title="row.insured_name">{{ row.insured_name || '—' }}</td>
            <td class="ident coverholder-col" :title="row.coverholder">{{ row.coverholder || '—' }}</td>
            <td class="date">{{ formatDate(row.date_of_loss) }}</td>

            <!-- Imported -->
            <td class="num l">{{ money(row.imported.indemnity) }}</td>
            <td class="num">{{ money(row.imported.fees) }}</td>

            <!-- LimeIQ cash call - opens with the date it was submitted -->
            <td class="date l">{{ formatDate(row.submitted) }}</td>
            <td class="num">{{ money(row.cash_call.indemnity) }}</td>
            <td class="num">{{ money(row.cash_call.fees) }}</td>

            <!-- Mark as Paid? toggle. Deliberately NOT b-form-checkbox: the
                 app's global _custom-control.scss repositions every
                 .custom-control-label pseudo-element with negative offsets and
                 nudges any inner <span>, which pushed the switch out of this
                 cell and forced its track back to a square. A plain button
                 owns its own geometry and cannot be reached by those rules. -->
            <td class="mid mark-col l">
              <button
                type="button"
                class="cc-switch"
                :class="{ 'cc-switch--on': row.status === 'Paid', 'cc-switch--locked': isAlreadyPaid(row) }"
                :aria-pressed="row.status === 'Paid' ? 'true' : 'false'"
                :disabled="isAlreadyPaid(row)"
                :title="isAlreadyPaid(row)
                  ? 'Already paid on the Cash Call page'
                  : (row.status === 'Paid' ? 'Mark as Outstanding' : 'Mark as Paid')"
                @click="toggleStatus(index)"
              >
                <span class="cc-switch__track"><span class="cc-switch__knob" /></span>
                <span class="cc-switch__text">{{ row.status }}</span>
              </button>
              <!-- LIQ-2412: the payment this export will record. Paid in full on
                   the last day of the reporting period unless changed; the link
                   opens the Mark as Paid popup for a different date or a partial
                   payment. -->
              <div v-if="row.payment" class="cc-paid-meta">
                <span class="cc-paid-meta__text" :title="paymentSummary(row)">{{ paymentSummary(row) }}</span>
                <button type="button" class="cc-paid-meta__link" @click="openPaymentModal(index)">
                  Change
                </button>
              </div>
            </td>

            <!-- Cash Call Action -->
            <td class="mid l">
              <b-form-select
                v-model="row.action"
                :options="actionOptions"
                size="sm"
                class="action-select"
                :class="{ 'action-select--matched': isMatched(row) }"
                @change="onActionChange(index)"
              />
            </td>

            <!-- Export Paid This Month (derived, or editable for Other) -->
            <td class="num l" :class="{ 'has-input': isOther(row) }">
              <b-form-input
                v-if="isOther(row)"
                :value="row.display.export.indemnity"
                type="text"
                inputmode="decimal"
                size="sm"
                class="num-input"
                @input="amountInput(index, 'export', 'indemnity', $event)"
              />
              <span v-else :class="{ matched: isMatchedComponent(row, 'indemnity') }">
                {{ money(derivedFigure(row, 'indemnity', 'export_paid_this_month')) }}
              </span>
            </td>
            <td class="num" :class="{ 'has-input': isOther(row) }">
              <b-form-input
                v-if="isOther(row)"
                :value="row.display.export.fees"
                type="text"
                inputmode="decimal"
                size="sm"
                class="num-input"
                @input="amountInput(index, 'export', 'fees', $event)"
              />
              <span v-else :class="{ matched: isMatchedComponent(row, 'fees') }">
                {{ money(derivedFigure(row, 'fees', 'export_paid_this_month')) }}
              </span>
            </td>

            <!-- Included in Totals (derived, or editable for Other) -->
            <td class="num l" :class="{ 'has-input': isOther(row) }">
              <b-form-input
                v-if="isOther(row)"
                :value="row.display.totals.indemnity"
                type="text"
                inputmode="decimal"
                size="sm"
                class="num-input"
                @input="amountInput(index, 'totals', 'indemnity', $event)"
              />
              <span v-else :class="{ matched: isMatchedComponent(row, 'indemnity') }">
                {{ money(derivedFigure(row, 'indemnity', 'included_in_totals')) }}
              </span>
            </td>
            <td class="num" :class="{ 'has-input': isOther(row) }">
              <b-form-input
                v-if="isOther(row)"
                :value="row.display.totals.fees"
                type="text"
                inputmode="decimal"
                size="sm"
                class="num-input"
                @input="amountInput(index, 'totals', 'fees', $event)"
              />
              <span v-else :class="{ matched: isMatchedComponent(row, 'fees') }">
                {{ money(derivedFigure(row, 'fees', 'included_in_totals')) }}
              </span>
            </td>
          </tr>

          <!-- "Other" reason capture (story 4.3) — one line under the row that
               selected it, since a reason is required for the audit trail. -->
          <template v-for="{ row, index } in visibleRows">
            <tr v-if="isOther(row)" :key="rowKey(row) + '-reason'" class="reason-row">
              <td colspan="15">
                <label class="reason-label">Reason for manual entry</label>
                <b-form-input
                  v-model="row.overrides.reason"
                  size="sm"
                  class="reason-input"
                  placeholder="Why these figures were entered manually"
                  :state="row.overrides.reason ? null : false"
                  @input="emitSelection"
                />
              </td>
            </tr>
          </template>
        </tbody>
      </table>

        <!-- A filter that matches nothing must say so, rather than leaving an
             empty table that reads like "no cash calls matched this export". -->
        <p v-if="!visibleRows.length && !visibleMismatchedRows.length" class="cc-no-match">
          No cash calls match the current filters.
        </p>
      </div>
    </div>

    <!-- LIQ-2412: Mark as Paid, replicating the Cash Call page's popup so a
         partial payment or a different date can be recorded here. Nothing is
         saved from the popup itself: the payment is recorded on the Cash Call
         when the export runs, like every other decision on this step. -->
    <b-modal
      v-model="paymentModal.show"
      title="Mark as Paid"
      centered
      no-close-on-backdrop
      dialog-class="cc-pay-modal"
    >
      <template v-if="paymentModal.row">
        <div class="cc-pay-summary">
          <div>
            <strong>Total Requested Amount:</strong>
            <span class="cc-pay-money">{{ currency(modalContext.requested.total) }}</span>
          </div>
          <div class="cc-pay-breakdown">
            <span><label>Indemnity:</label> {{ currency(modalContext.requested.indemnity) }}</span>
            <span><label>Fees:</label> {{ currency(modalContext.requested.fees) }}</span>
          </div>
          <!-- Installments: only once something has already been paid, so a
               cash call settled in one go looks as it does on the Cash Call page. -->
          <div v-if="modalContext.paid.total > 0" class="cc-pay-breakdown">
            <span><label>Already Paid:</label> {{ currency(modalContext.paid.total) }}</span>
            <span><label>Remaining:</label> {{ currency(modalContext.remaining) }}</span>
          </div>
        </div>

        <div v-if="modalContext.payments.length" class="cc-pay-history">
          <label>Payments recorded</label>
          <table>
            <tr v-for="(p, i) in modalContext.payments" :key="i">
              <td>
                <span v-if="p.received_date">Rcvd {{ p.received_date }} · </span>
                Paid {{ p.paid_date || '—' }}
              </td>
              <td class="cc-pay-history__amt">{{ currency(p.amount) }}</td>
              <td class="cc-pay-history__cmt">{{ p.comment }}</td>
            </tr>
          </table>
        </div>

        <div class="cc-pay-field">
          <label>Payment Amount</label>
          <select v-model="paymentModal.form.payment_type" class="custom-select cc-pay-select">
            <option value="FULL">Full</option>
            <option value="PARTIAL">Partial</option>
          </select>
        </div>

        <template v-if="paymentModal.form.payment_type === 'PARTIAL'">
          <div class="cc-pay-field">
            <label>Paid Indemnity</label>
            <input
              :value="paymentModal.form.paid_indemnity"
              class="cc-pay-input"
              placeholder="0.00"
              @input="modalMoneyInput('paid_indemnity', $event.target.value)"
            />
          </div>

          <div v-if="modalFeeFields.length" class="cc-pay-field">
            <label>Paid Fees Breakdown</label>
            <div class="cc-pay-fee-grid">
              <div v-for="f in modalFeeFields" :key="f.key" class="cc-pay-fee-item">
                <label class="cc-pay-sub-label">{{ f.label }}</label>
                <input
                  :value="paymentModal.form.fees[f.key]"
                  class="cc-pay-input"
                  placeholder="0.00"
                  @input="modalMoneyInput(f.key, $event.target.value)"
                />
              </div>
            </div>
          </div>

          <div class="cc-pay-total">
            <span><strong>Total Paid Indemnity:</strong> {{ currency(modalIndemnity) }}</span>
            <span class="cc-pay-total__divider">|</span>
            <span><strong>Total Paid Fees:</strong> {{ currency(modalFeeTotal) }}</span>
          </div>
        </template>

        <!-- Two payment dates, as on the Cash Call page (LIQ-2017). -->
        <div class="cc-pay-dates">
          <div class="cc-pay-field">
            <label>Cash Call Received Date</label>
            <DatePicker
              v-model="paymentModal.form.received_date"
              :format="dateFormat"
              value-type="format"
              :disabled-date="isFutureDate"
              class="cc-pay-datepicker"
            />
          </div>
          <div class="cc-pay-field">
            <label>Paid to Insured Date</label>
            <DatePicker
              v-model="paymentModal.form.paid_date"
              :format="dateFormat"
              value-type="format"
              :clearable="false"
              :disabled-date="isDisallowedPaidDate"
              class="cc-pay-datepicker"
            />
          </div>
        </div>

        <div class="cc-pay-field">
          <label>Comment<span v-if="modalContext.is_co_lead"> (optional)</span></label>
          <textarea
            v-model="paymentModal.form.comment"
            class="cc-pay-textarea"
            placeholder="Enter comment..."
          ></textarea>
        </div>

        <p v-if="paymentModal.error" class="cc-pay-error">{{ paymentModal.error }}</p>
        <p class="cc-pay-note">This payment is recorded on the Cash Call when the export runs.</p>
      </template>

      <template #modal-footer>
        <b-button class="btn-grey" @click="paymentModal.show = false">Cancel</b-button>
        <b-button variant="primary" @click="savePaymentModal">Save</b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import * as XLSX from 'xlsx'
import DatePicker from 'vue2-datepicker'

import { DATE_FORMAT } from '@constants'
import ReportsService from '@services/reportsService'

// Cash Call Action labels — must match the backend's ACTION_* constants
// exactly (azure_ad_auth/cash_call_reconciliation.py), since the confirmed
// action string is sent straight back for the export to apply.
const ACTION_CASH_CALL_INCLUDED = 'LimeIQ Cash Call included in export'
const ACTION_MATCHED = 'Matched'
const ACTION_CASH_CALL_AND_ADDITIONAL = 'LimeIQ Cash Call & additional included in export'
const ACTION_MATCHED_AND_ADDITIONAL = 'Matched & additional included in export'
const ACTION_NO_ACTION = 'No Action'
const ACTION_OTHER = 'Other'

// Column headings keyed by filter field, so an applied filter can be named in
// the toolbar the same way the reader saw it in the header.
const FILTER_LABELS = {
  claim_reference: 'Claim Ref',
  limeiq_key: 'LimeIQ Key',
  limeiq_reference: 'LimeIQ Reference',
  insured_name: 'Insured Name',
  coverholder: 'Coverholder',
  date_of_loss: 'Date of Loss',
  imported_indemnity: 'Imported Indemnity',
  imported_fees: 'Imported Fees',
  submitted: 'Submitted',
  cash_call_indemnity: 'Cash Call Indemnity',
  cash_call_fees: 'Cash Call Fees',
  status: 'Mark as Paid?',
  action: 'Cash Call Action',
  export_indemnity: 'Export Paid Indemnity',
  export_fees: 'Export Paid Fees',
  totals_indemnity: 'Included in Totals Indemnity',
  totals_fees: 'Included in Totals Fees',
}

const MATCH_TOLERANCE = 0.01

// The five fee types a cash call can request - same keys and labels as the Cash
// Call page's Mark as Paid, and as the backend's payment rows.
const FEE_FIELDS = [
  { key: 'tpa_fees_amount', label: 'TPA Fees' },
  { key: 'adjuster_fee_amount', label: 'Adjusters Fees' },
  { key: 'defence_fees_amount', label: 'Defence Fees' },
  { key: 'expenses_amount', label: 'Expenses' },
  { key: 'attorney_coverage_amount', label: 'Attorney Coverage' },
]

// A calendar date as YYYY-MM-DD in the browser's own timezone.
function localIso(date) {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

function parseMoney(value) {
  const amount = parseFloat(String(value == null ? '' : value).replace(/,/g, ''))
  return isNaN(amount) ? 0 : amount
}

/**
 * One column heading, with its own search control.
 *
 * Rendered as a functional-ish local component rather than 17 copies of the
 * same markup: each head owns a popover holding that column's filter, and the
 * icon renders active while the column is filtering, so an applied filter is
 * visible without opening anything.
 *
 * A column whose values come from a fixed list (Mark as Paid?, Cash Call
 * Action) takes `options` and renders a select; every other column takes free
 * text matched on substring.
 */
const ColumnHead = {
  name: 'ColumnHead',
  props: {
    label: { type: String, required: true },
    field: { type: String, required: true },
    // Present for fixed-value columns; absent means free-text.
    options: { type: Array, default: null },
    align: { type: String, default: 'left' },
    // Date columns filter with a picker rather than free text.
    date: { type: Boolean, default: false },
  },
  inject: ['columnFilters', 'setColumnFilter'],
  computed: {
    value() {
      return this.columnFilters[this.field] || ''
    },
    isActive() {
      return this.value !== ''
    },
    popoverId() {
      return `cc-filter-${this.field}`
    },
  },
  methods: {
    onInput(event) {
      this.setColumnFilter(this.field, event.target.value)
    },
    // A native date input speaks yyyy-mm-dd, but the filter matches what the
    // cell DISPLAYS, which is dd/mm/yyyy - so the picked value is converted
    // on the way in, or it would match nothing at all.
    onDateInput(event) {
      const iso = event.target.value
      if (!iso) {
        this.setColumnFilter(this.field, '')
        return
      }
      const [y, m, d] = iso.split('-')
      this.setColumnFilter(this.field, `${d}/${m}/${y}`)
    },
    // The reverse, to show the picker the date it is already filtering on.
    isoValue() {
      const parts = this.value.split('/')
      if (parts.length !== 3) return ''
      const [d, m, y] = parts
      return `${y}-${m}-${d}`
    },
    clear() {
      this.setColumnFilter(this.field, '')
    },
  },
  // A render function, NOT a `template` string: the app builds against the
  // runtime-only Vue bundle (no runtimeCompiler in vue.config.js), which cannot
  // compile a template at runtime - it would silently render nothing.
  render(h) {
    const label = h('span', { class: 'cc-head__label' }, this.label)

    // An inline SVG, not a glyph: U+2315 (the telephone-recorder/search symbol)
    // is missing from most UI fonts and renders as a tofu box or a wildly
    // mis-sized character next to the heading.
    const magnifier = h('svg', {
      attrs: {
        viewBox: '0 0 16 16',
        width: '11',
        height: '11',
        fill: 'none',
        stroke: 'currentColor',
        'stroke-width': '2',
        'stroke-linecap': 'round',
        'aria-hidden': 'true',
        focusable: 'false',
      },
    }, [
      h('circle', { attrs: { cx: '7', cy: '7', r: '4.5' } }),
      h('line', { attrs: { x1: '10.5', y1: '10.5', x2: '14.5', y2: '14.5' } }),
    ])

    const icon = h('button', {
      attrs: {
        id: this.popoverId,
        type: 'button',
        title: this.isActive ? `Filtering: ${this.value}` : `Search ${this.label}`,
      },
      class: ['cc-head__icon', { 'is-active': this.isActive }],
    }, [magnifier])

    const control = this.date
      ? h('input', {
        class: 'cc-filter-pop__input',
        attrs: { type: 'date' },
        domProps: { value: this.isoValue() },
        on: { input: this.onDateInput },
      })
      : this.options
      ? h('select', {
        class: 'cc-filter-pop__input',
        domProps: { value: this.value },
        on: { change: this.onInput },
      }, [
        h('option', { domProps: { value: '' } }, 'All'),
        ...this.options.map((opt) => h('option', {
          key: opt,
          domProps: { value: opt },
        }, opt)),
      ])
      : h('input', {
        class: 'cc-filter-pop__input',
        attrs: { placeholder: `Search ${this.label}` },
        domProps: { value: this.value },
        on: { input: this.onInput },
      })

    const body = [control]
    if (this.isActive) {
      body.push(h('button', {
        attrs: { type: 'button' },
        class: 'cc-filter-pop__clear',
        on: { click: this.clear },
      }, 'Clear'))
    }

    const popover = h('b-popover', {
      props: {
        target: this.popoverId,
        triggers: 'click blur',
        placement: 'bottom',
        customClass: 'cc-filter-pop',
      },
    }, [h('div', { class: 'cc-filter-pop__body' }, body)])

    // The icon is always last, so it always renders to the RIGHT of its label,
    // in every column regardless of alignment.
    return h('span', {
      class: ['cc-head', `cc-head--${this.align}`],
    }, [label, icon, popover])
  },
}

export default {
  name: 'SaveReportWizardCashCallReconciliation',
  components: { ColumnHead, DatePicker },
  // The heads live inside <th> cells, so props would have to be threaded
  // through the table markup; provide/inject keeps that wiring out of it.
  provide() {
    return {
      columnFilters: this.columnFilters,
      setColumnFilter: this.setColumnFilter,
    }
  },
  props: {
    // The export filter payload — same shape the export itself is given.
    exportPayload: { type: Object, default: () => ({}) },
    // Full Data Exports bypass this step entirely (story section 2).
    isFullDataExport: { type: Boolean, default: false },
    // The decisions the wizard already holds for this export, fed back in so a
    // re-created component restores them instead of reverting to the server's
    // suggestions. Empty on a genuinely new export.
    confirmedRows: { type: Array, default: () => [] },
  },
  data() {
    return {
      loading: false,
      error: null,
      // Numbers each scan so only the newest one's answer is applied - see
      // fetchRows.
      scanSeq: 0,
      rows: [],
      actionOptions: [
        ACTION_CASH_CALL_INCLUDED,
        ACTION_MATCHED,
        ACTION_CASH_CALL_AND_ADDITIONAL,
        ACTION_MATCHED_AND_ADDITIONAL,
        ACTION_NO_ACTION,
        ACTION_OTHER,
      ],
      // Full explanations live in the (i) tooltip, so the table itself only
      // shows the short action names (story 4.1).
      actionHelp: [
        { label: ACTION_CASH_CALL_INCLUDED, help: 'No amount on the imported BDX row. The LimeIQ cash call is populated into Paid This Month on the row and excluded from the column totals, so it is never double counted.' },
        { label: ACTION_MATCHED, help: 'Imported amount equals the LimeIQ cash call. The cash call overrides the row and nets off against the column totals.' },
        { label: ACTION_CASH_CALL_AND_ADDITIONAL, help: 'Imported amount is less than the cash call. Both are shown on the row; only the lesser imported figure is included in the column totals.' },
        { label: ACTION_MATCHED_AND_ADDITIONAL, help: 'Imported amount is greater than the cash call. The full imported amount is shown on the row; only the excess above the cash call is included in the column totals.' },
        { label: ACTION_NO_ACTION, help: 'Imported data retained as-is. No cash call adjustment to the row or the totals. Default where the cash call is Outstanding.' },
        { label: ACTION_OTHER, help: 'The TPA enters the figures that should appear on the export and in the totals. A reason is captured and logged against the export session.' },
      ],
      // One filter per column, keyed by the field names the heads pass in.
      // Held as plain strings so an empty value means "no filter" with no
      // special cases anywhere downstream.
      columnFilters: {
        claim_reference: '',
        limeiq_key: '',
        limeiq_reference: '',
        insured_name: '',
        coverholder: '',
        date_of_loss: '',
        imported_indemnity: '',
        imported_fees: '',
        submitted: '',
        cash_call_indemnity: '',
        cash_call_fees: '',
        status: '',
        action: '',
        export_indemnity: '',
        export_fees: '',
        totals_indemnity: '',
        totals_fees: '',
      },
      statusOptions: ['Paid', 'Outstanding'],
      dateFormat: DATE_FORMAT,
      // LIQ-2412 Mark as Paid popup. `index` is the row's ORIGINAL index, like
      // every other handler here; `form` is the popup's working copy, applied
      // to the row only on Save.
      paymentModal: {
        show: false,
        index: null,
        row: null,
        error: '',
        form: {
          payment_type: 'FULL',
          paid_indemnity: '',
          fees: {},
          paid_date: '',
          received_date: '',
          comment: '',
        },
      },
    }
  },
  computed: {
    tooltipId() {
      return 'cash-call-action-help'
    },
    activeFilterCount() {
      return Object.values(this.columnFilters).filter((value) => value !== '').length
    },
    // A count alone ("1 filter") does not say WHICH column is filtering or on
    // what, so a reader seeing 5 of 7 rows cannot tell what is being hidden.
    // Each active filter is named, with its value, and can be lifted on its own.
    activeFilters() {
      return Object.entries(this.columnFilters)
        .filter(([, value]) => value !== '')
        .map(([field, value]) => ({
          field,
          value,
          label: FILTER_LABELS[field] || field,
        }))
    },
    hasActiveFilter() {
      return this.activeFilterCount > 0
    },
    visibleRows() {
      // Actionable rows only. Flagged ones are rendered by their own loop and
      // are excluded here so nothing that walks visibleRows - the Excel export,
      // the empty-table message, the reason-row loop - treats a row with no
      // figures and no action as if it had them.
      return this.applyFilters(
        this.rows
          .map((row, index) => ({ row, index }))
          .filter(({ row }) => !row.is_mismatch)
      )
    },
    actionableRowCount() {
      return this.rows.filter((row) => !row.is_mismatch).length
    },
    visibleMismatchedRows() {
      // Cash calls whose claim reference matched this export but whose LimeIQ
      // key did not. Filtered by the same column filters so the table stays
      // coherent when a filter is applied.
      return this.applyFilters(
        this.rows
          .map((row, index) => ({ row, index }))
          .filter(({ row }) => row.is_mismatch)
      )
    },
    scanSignature() {
      // A stable digest of the filters that change WHICH claim rows the scan
      // returns. Only these warrant discarding the user's decisions and
      // re-scanning; anything else in the payload (column choices, filename
      // context, the reconciliation this step itself emits) must not.
      const payload = this.exportPayload || {}
      return JSON.stringify([
        payload.Unique_Market_Reference_UMR,
        payload.Reporting_Period_End_Date,
        payload.jurisdiction,
        payload.bordereaux_status,
        payload.company_id,
        payload.uw_option,
        payload.export_type,
        payload.role,
        payload.role_value,
      ])
    },
    // The popup's figures, in the row's own terms (a Co-Lead key's share). A
    // row from an older scan carries no context, so fall back to empty figures
    // rather than throwing inside the template.
    modalContext() {
      const row = this.paymentModal.row
      const context = (row && row.payment_context) || {}
      return {
        is_co_lead: !!context.is_co_lead,
        requested: context.requested || { total: 0, indemnity: 0, fees: 0 },
        paid: context.paid || { total: 0, indemnity: 0, fees: 0 },
        paid_split: context.paid_split || { indemnity: 0, fees: 0 },
        remaining: Number(context.remaining || 0),
        payments: context.payments || [],
      }
    },
    // Only the fees actually requested and still outstanding, each capped at
    // what is left on it - the same rule as the Cash Call page.
    modalFeeFields() {
      const { requested, paid } = this.modalContext
      return FEE_FIELDS
        .map((f) => ({
          ...f,
          outstanding: Math.max(0, Number(requested[f.key] || 0) - Number(paid[f.key] || 0)),
        }))
        .filter((f) => f.outstanding > 0)
    },
    modalIndemnity() {
      return parseMoney(this.paymentModal.form.paid_indemnity)
    },
    modalFeeTotal() {
      return this.modalFeeFields.reduce(
        (sum, f) => sum + parseMoney(this.paymentModal.form.fees[f.key]), 0,
      )
    },
    todayIso() {
      return localIso(new Date())
    },
  },
  watch: {
    // Re-scan whenever the user changes the FILTERS that decide which claim
    // rows this export produces - and only those.
    //
    // A deep watcher on the whole payload re-fetched on any mutation of it,
    // including ones this step itself caused, which silently rehydrated the
    // table from the server's suggestions and discarded every Paid/Outstanding
    // toggle and Cash Call Action the user had set. Watching a digest of just
    // the scan-relevant filters keeps the intended behaviour (change a filter,
    // come back, see a fresh scan) without throwing away the user's decisions.
    scanSignature: {
      handler(next, previous) {
        if (next === previous) return
        this.fetchRows()
      },
    },
  },
  mounted() {
    this.fetchRows()
  },
  beforeDestroy() {
    // Leaving the step: an answer still in flight must not land on (and emit
    // from) this destroyed instance.
    this.scanSeq += 1
  },
  methods: {
    rowKey(row) {
      return `${row.cash_call_key_id}-${row.claim_reference}`
    },
    money(value) {
      // No currency symbols anywhere on the table; nil shows as an em dash
      // (story 4.2). A *nil* is the absence of a figure - null/blank - and is
      // not the same as a netted-off zero: story scenarios 2 and 3 require
      // "Included in Totals shows 0.00", so a real zero must print as 0.00.
      if (value === null || value === undefined || value === '') return '—'
      const amount = Number(value)
      if (isNaN(amount)) return '—'
      return amount.toLocaleString(undefined, {
        minimumFractionDigits: 2, maximumFractionDigits: 2,
      })
    },
    formatDate(value) {
      if (!value) return '—'
      const date = new Date(value)
      if (isNaN(date.getTime())) return '—'
      const dd = String(date.getDate()).padStart(2, '0')
      const mm = String(date.getMonth() + 1).padStart(2, '0')
      return `${dd}/${mm}/${date.getFullYear()}`
    },
    derivedFigure(row, component, field) {
      // Story 4.3 / matrix scenario 6 - "Outstanding with no import": where the
      // cash call is Outstanding and nothing was imported on the row, both
      // derived figures are NIL, not 0.00. An outstanding cash call does not
      // reach the export, and there is no imported figure to retain either, so
      // there is nothing to state. Every other zero is a real netted-off 0.00.
      const derived = (row.derived && row.derived[component]) || {}
      const imported = Number((row.imported && row.imported[component]) || 0)
      if (row.status !== 'Paid' && !imported && !this.isOther(row)) return null
      return derived[field]
    },
    isOther(row) {
      return row.action === ACTION_OTHER
    },
    isMatched(row) {
      return row.action === ACTION_MATCHED
    },
    isMatchedComponent(row, component) {
      // Matched is the only action given a colour (green); every other figure
      // renders as standard text (story 4.4).
      return row.derived
        && row.derived[component]
        && row.derived[component].action === ACTION_MATCHED
    },
    async fetchRows() {
      if (this.isFullDataExport) {
        this.rows = []
        this.emitSelection()
        return
      }
      // Two scans can be in flight at once - coming back from Select Filters
      // mounts this step (one scan) while the filters settle and trip the
      // scanSignature watcher (another). Whichever answered LAST used to win,
      // so a stale failure could replace a good table with "Could not load…"
      // until Retry. Only the newest scan's answer, success or failure, is
      // applied; older ones are dropped.
      const seq = ++this.scanSeq
      this.loading = true
      this.error = null
      try {
        const data = await ReportsService.scanCashCallReconciliation(this.exportPayload)
        if (seq !== this.scanSeq) return
        const rows = (data && data.rows) || []
        this.rows = rows.map((row) => this.hydrate(row))
        this.restoreConfirmedDecisions()
      } catch (e) {
        if (seq !== this.scanSeq) return
        this.error = (e && e.response && e.response.data
          && (e.response.data.message || e.response.data.error))
          || 'Could not load cash call reconciliation for this export.'
        this.rows = []
      } finally {
        if (seq === this.scanSeq) {
          this.loading = false
          this.emitSelection()
        }
      }
    },
    restoreConfirmedDecisions() {
      // Re-apply the decisions the wizard is already holding for this export.
      //
      // This component is destroyed whenever the step is left (v-if) and a
      // fresh mount re-scans, so without this every Paid/Outstanding toggle and
      // Cash Call Action would silently revert to the server's suggestion
      // before the user ever reached Export/Save.
      //
      // Matched per ROW - cash_call_key_id, stable across scans. A Co-Lead cash
      // call has one row per UMR, so matching on cash_call_id kept only one
      // decision per cash call and copied it onto every one of its UMRs. A
      // decision saved before cash_call_key_id was emitted still restores by
      // cash_call_id. A row the scan no longer returns is simply skipped.
      const byKey = new Map()
      const byCashCall = new Map()
      for (const row of this.confirmedRows || []) {
        if (!row) continue
        if (row.cash_call_key_id != null) byKey.set(row.cash_call_key_id, row)
        else if (row.cash_call_id != null) byCashCall.set(row.cash_call_id, row)
      }
      if (!byKey.size && !byCashCall.size) return 0

      let restored = 0
      this.rows.forEach((row, index) => {
        // A flagged "key does not match" row carries no figures, action or
        // payment - never a decision to restore. It shares its cash_call_id with
        // the cash call's matched rows, and recomputing it read figures it does
        // not have: the throw landed in fetchRows' catch, showed "Could not
        // load…" and emitted an empty selection that wiped every decision.
        if (row.is_mismatch) return
        const saved = byKey.get(row.cash_call_key_id) || byCashCall.get(row.cash_call_id)
        if (!saved) return
        // Only the user's own inputs are carried over; every derived figure is
        // recomputed from them so the numbers can never drift out of step.
        // Bring back a status only where the USER changed it on this page.
        // A saved status that merely repeated what an earlier scan said is not
        // a decision - re-applying it over a fresh scan resurrects stale state:
        // a cash call un-paid on the Cash Call page since the last visit came
        // back Paid, with no payment to record, so the export would carry its
        // money while the Cash Call page stayed Submitted. A row saved before
        // scanned_status existed keeps the old behaviour.
        const userChangedStatus = saved.scanned_status
          ? saved.status !== saved.scanned_status
          : true
        if (saved.status && userChangedStatus) row.status = saved.status
        // An already-Paid cash call is locked on Paid, so a decision saved
        // before the lock (Outstanding) must not bring it back unlocked.
        if (this.isAlreadyPaid(row)) row.status = 'Paid'
        // LIQ-2412: the payment chosen for this row (default or popup), only
        // ever on a cash call that was outstanding when scanned.
        row.payment = saved.payment && row.originalStatus !== 'Paid'
          ? { ...saved.payment, fees: { ...(saved.payment.fees || {}) } }
          : null
        this.applyPaymentFigures(row)
        // The row's suggestion was made for the status it was SCANNED with;
        // re-make it for the restored status, or a row toggled to Paid would
        // come back carrying the Outstanding suggestion.
        this.resuggest(row)

        // Restore the action ONLY where the user deliberately overrode the
        // suggestion. Restoring a saved action that merely WAS the suggestion
        // pins the row to a stale one: if the underlying amounts have since
        // changed, the row would keep claiming the old action instead of
        // adopting the correct new one (e.g. amounts edited to match exactly
        // should become "Matched", not stay on the previous suggestion).
        const wasOverridden = saved.action
          && saved.suggested_action
          && saved.action !== saved.suggested_action
        if (wasOverridden) row.action = saved.action
        if (saved.reason) row.overrides.reason = saved.reason
        // Typed figures belong to "Other" alone. Restoring them for any other
        // action would resurrect numbers the user never entered against it.
        if (row.action === ACTION_OTHER) {
          if (saved.export_paid_this_month) {
            row.overrides.export.indemnity = saved.export_paid_this_month.indemnity || 0
            row.overrides.export.fees = saved.export_paid_this_month.fees || 0
          }
          if (saved.included_in_totals) {
            row.overrides.totals.indemnity = saved.included_in_totals.indemnity || 0
            row.overrides.totals.fees = saved.included_in_totals.fees || 0
          }
        }
        this.recompute(index)
        restored += 1
      })
      return restored
    },
    hydrate(row) {
      // A flagged row has no suggestion, no figures and no controls, so it is
      // passed through untouched. Giving it the defaults below would hand it a
      // status and an action it must never carry - and isActionedHere compares
      // against originalStatus, which would then mark it as actioned here.
      if (row.is_mismatch) return { ...row }

      // The backend suggests an action per component. The dropdown is a single
      // per-row control, so the row adopts the indemnity suggestion as its
      // selected action while each component keeps its own derived figures —
      // which is what preserves "indemnity and fees evaluated independently".
      const suggestion = row.suggestion || {}
      return {
        ...row,
        action: (suggestion.indemnity && suggestion.indemnity.action) || ACTION_NO_ACTION,
        suggestedAction: (suggestion.indemnity && suggestion.indemnity.action) || ACTION_NO_ACTION,
        derived: {
          indemnity: { ...(suggestion.indemnity || {}) },
          fees: { ...(suggestion.fees || {}) },
        },
        overrides: {
          export: { indemnity: 0, fees: 0 },
          totals: { indemnity: 0, fees: 0 },
          reason: '',
        },
        // What the "Other" fields show while being typed: the comma-grouped
        // form of `overrides`, kept separate so the stored value stays numeric.
        display: {
          export: { indemnity: '', fees: '' },
          totals: { indemnity: '', fees: '' },
        },
        // The status this cash call already had on the Cash Call page when the
        // scan ran. Anything differing from it was actioned HERE, which is what
        // the bold styling marks - a cash call that was already Paid before
        // this export must not be presented as though this page paid it.
        originalStatus: row.status,
        // LIQ-2412: the payment this export will record against the cash call
        // - null until the row is marked Paid here (or a partial installment
        // is entered in the popup).
        payment: null,
        // The cash call figures as scanned, so a partial payment can be undone
        // back to them.
        scannedCashCall: { ...(row.cash_call || {}) },
      }
    },
    isActionedHere(row) {
      return row.status !== row.originalStatus || !!row.payment
    },
    currency(value) {
      return Number(value || 0).toLocaleString('en-US', {
        style: 'currency', currency: 'USD', minimumFractionDigits: 2,
      })
    },
    defaultPaidDate(row) {
      // LIQ-2412 (Release/Feedback 10.1): a cash call marked paid at the export
      // step is dated the last day of the previous month - exporting on 16
      // October for the September BDX dates it 30 September, so the payment
      // falls before the month the export is run in.
      const now = new Date()
      let date = localIso(new Date(now.getFullYear(), now.getMonth(), 0))
      // Never before the cash call was raised - the Cash Call page's Submitted
      // Date rule, which Ryan confirmed applies here too. A cash call raised
      // this month is dated the day it was raised.
      const submitted = this.submittedIso(row)
      if (submitted && date < submitted) date = submitted
      // Never a future date - the Cash Call page refuses those.
      return date > this.todayIso ? this.todayIso : date
    },
    submittedIso(row) {
      // The cash call's submitted date on the TPA's own calendar, as the Cash
      // Call page compares it - submitted_on is a UTC timestamp, so reading it
      // as a plain date would put an evening US submission on the next day.
      if (!row || !row.submitted) return ''
      const date = new Date(row.submitted)
      return isNaN(date.getTime()) ? '' : localIso(date)
    },
    isDisallowedPaidDate(date) {
      // The Paid to Insured Date picker: nothing in the future, nothing before
      // the cash call was submitted - the same days the Cash Call page greys out.
      const day = localIso(date)
      const submitted = this.submittedIso(this.paymentModal.row)
      return day > this.todayIso || (!!submitted && day < submitted)
    },
    defaultPayment(row) {
      return {
        payment_type: 'FULL',
        paid_date: this.defaultPaidDate(row),
        received_date: '',
        comment: '',
        paid_indemnity: 0,
        fees: {},
      }
    },
    paymentTotal(payment) {
      return Number(payment.paid_indemnity || 0) + FEE_FIELDS.reduce(
        (sum, f) => sum + Number((payment.fees || {})[f.key] || 0), 0,
      )
    },
    paymentSummary(row) {
      const payment = row.payment
      // Split rather than parsed: new Date('2026-09-30') is UTC midnight, which
      // reads as 29/09 anywhere west of UTC.
      const [y, m, d] = String(payment.paid_date || '').split('-')
      const date = d ? `${d}/${m}/${y}` : '—'
      if (payment.payment_type !== 'PARTIAL') return `In full · ${date}`
      const amount = this.money(this.paymentTotal(payment))
      // Named in the popup's own words: an Outstanding row carrying a partial
      // is one the user answered "No, awaiting funds" for; a Paid one, "Yes,
      // final payment".
      if (row.status !== 'Paid') return `Partial ${amount} (awaiting funds) · ${date}`
      return `Partial ${amount} (final) · ${date}`
    },
    applyPaymentFigures(row) {
      // The cash call figures the export nets on. Paid in full is the whole
      // request (what the scan shows for an outstanding cash call); a partial
      // marked final is what earlier installments covered plus this payment.
      // An installment leaves the cash call outstanding, so it keeps the
      // scanned figures - an outstanding cash call puts nothing on the export.
      const payment = row.payment
      if (payment && payment.payment_type === 'PARTIAL' && row.status === 'Paid') {
        const split = (row.payment_context && row.payment_context.paid_split) || {}
        const fees = FEE_FIELDS.reduce((sum, f) => sum + Number((payment.fees || {})[f.key] || 0), 0)
        row.cash_call = {
          indemnity: Number(split.indemnity || 0) + Number(payment.paid_indemnity || 0),
          fees: Number(split.fees || 0) + fees,
        }
      } else {
        row.cash_call = { ...row.scannedCashCall }
      }
    },
    resuggest(row) {
      // Re-run the auto-suggestion for the row's current status and figures,
      // so the action and both derived figures follow them (story AC3).
      const isPaid = row.status === 'Paid'
      const indemnity = this.suggestComponent(row.imported.indemnity, row.cash_call.indemnity, isPaid)
      const fees = this.suggestComponent(row.imported.fees, row.cash_call.fees, isPaid)
      row.derived = { indemnity, fees }
      row.action = indemnity.action
      row.suggestedAction = indemnity.action
    },
    isFutureDate(date) {
      return localIso(date) > this.todayIso
    },
    openPaymentModal(index) {
      const row = this.rows[index]
      const payment = row.payment || this.defaultPayment(row)
      const fees = {}
      FEE_FIELDS.forEach((f) => {
        const value = Number((payment.fees || {})[f.key] || 0)
        fees[f.key] = value ? value.toLocaleString('en-US') : ''
      })
      this.paymentModal = {
        show: true,
        index,
        row,
        error: '',
        form: {
          payment_type: payment.payment_type,
          paid_indemnity: payment.paid_indemnity ? Number(payment.paid_indemnity).toLocaleString('en-US') : '',
          fees,
          paid_date: payment.paid_date || this.defaultPaidDate(row),
          received_date: payment.received_date || '',
          comment: payment.comment || '',
        },
      }
    },
    modalMoneyInput(field, raw) {
      // Thousands separators as you type, as on the Cash Call page.
      const cleaned = String(raw == null ? '' : raw)
        .replace(/[^0-9.]/g, '')
        .replace(/\.(?=.*\.)/g, '')
      const [whole, decimals] = cleaned.split('.')
      let display = whole === '' ? '' : Number(whole).toLocaleString('en-US')
      if (decimals !== undefined) display += `.${decimals.slice(0, 2)}`
      if (field === 'paid_indemnity') {
        this.paymentModal.form.paid_indemnity = display
      } else {
        this.$set(this.paymentModal.form.fees, field, display)
      }
    },
    async savePaymentModal() {
      // Validates exactly what the Cash Call page's Mark as Paid validates,
      // then applies the payment to the row. Nothing is sent anywhere yet: the
      // export records it on the Cash Call when it runs.
      const { form, index } = this.paymentModal
      const row = this.rows[index]
      const context = this.modalContext
      const isPartial = form.payment_type === 'PARTIAL'
      const indemnity = isPartial ? this.modalIndemnity : 0
      const feeTotal = isPartial ? this.modalFeeTotal : 0
      const fail = (message) => { this.paymentModal.error = message }
      this.paymentModal.error = ''

      if (isPartial) {
        const indemnityCeiling = Math.max(0, context.requested.indemnity - context.paid.indemnity)
        if (context.is_co_lead) {
          if (indemnity + feeTotal <= 0) return fail('Enter a Paid Indemnity and/or fee amount.')
        } else if (indemnityCeiling > 0.01 && indemnity <= 0) {
          // Not required once the indemnity is fully covered by earlier
          // installments - the balance may be fees only.
          return fail('Valid paid indemnity is required')
        }
        if (indemnity > indemnityCeiling + 0.01) {
          return fail(`Paid Indemnity cannot exceed the outstanding amount (${this.currency(indemnityCeiling)})`)
        }
        for (const f of this.modalFeeFields) {
          if (parseMoney(form.fees[f.key]) > f.outstanding + 0.01) {
            return fail(`${f.label} cannot exceed the outstanding amount (${this.currency(f.outstanding)})`)
          }
        }
        const feeCeiling = Math.max(0, context.requested.fees - context.paid.fees)
        if (feeTotal > feeCeiling + 0.01) {
          return fail(`Total Paid Fee cannot exceed the outstanding fee amount (${this.currency(feeCeiling)})`)
        }
      }

      if (!form.paid_date) return fail('Select the Paid to Insured Date.')
      if (form.paid_date > this.todayIso) return fail('Paid Date cannot be a future date')
      const submitted = this.submittedIso(row)
      if (submitted && form.paid_date < submitted) {
        return fail('Paid Date cannot be earlier than Submitted Date')
      }
      if (form.received_date && form.received_date > form.paid_date) {
        return fail('Cash Call Received Date cannot be after Paid to Insured Date')
      }
      if (!context.is_co_lead && !String(form.comment || '').trim()) {
        return fail('Comment is required')
      }

      // A partial payment short of what is outstanding: ask whether it is the
      // final one, as the Cash Call page does. "No, awaiting funds" records an
      // installment and leaves the cash call outstanding.
      let isFinal = true
      if (isPartial) {
        const shortfall = context.remaining - (indemnity + feeTotal)
        if (shortfall > 0.01) {
          const subject = context.is_co_lead ? 'this UMR' : 'this cash call'
          const answer = await this.$bvModal.msgBoxConfirm(
            `Is this the final payment for ${subject}? Choosing "Yes" closes it and writes off the remaining ${this.currency(shortfall)}.`,
            {
              title: 'Partial Payment',
              okTitle: 'Yes, final payment',
              cancelTitle: 'No, awaiting funds',
              okVariant: 'primary',
              centered: true,
            },
          )
          // Esc / backdrop - not decided, so leave the popup open.
          if (answer === null) return
          isFinal = answer === true
        }
      }

      const fees = {}
      if (isPartial) {
        this.modalFeeFields.forEach((f) => { fees[f.key] = parseMoney(form.fees[f.key]) })
      }
      row.payment = {
        payment_type: form.payment_type,
        paid_date: form.paid_date,
        received_date: form.received_date || '',
        comment: String(form.comment || '').trim(),
        paid_indemnity: indemnity,
        fees,
      }
      row.status = isFinal ? 'Paid' : 'Outstanding'
      this.applyPaymentFigures(row)
      this.resuggest(row)
      this.paymentModal.show = false
      this.emitSelection()
    },
    applyFilters(entries) {
      // Shared by visibleRows and visibleMismatchedRows so both obey the column
      // filters identically. Entries carry each row's ORIGINAL index, because
      // every handler mutates this.rows[index] - renumbering on filter would
      // toggle the wrong cash call.
      if (!this.hasActiveFilter) return entries

      const filters = Object.entries(this.columnFilters)
        .filter(([, value]) => value !== '')

      return entries.filter(({ row }) => filters.every(([field, value]) => {
        // Status and Action come from a fixed list, so they match exactly;
        // every other column matches on substring.
        if (field === 'status' || field === 'action') {
          return String(row[field] || '') === value
        }
        return this.columnText(row, field)
          .toLowerCase()
          .includes(String(value).toLowerCase())
      }))
    },
    columnText(row, field) {
      // What a column actually DISPLAYS, so a filter matches what the user can
      // see: typing "500,000" finds the row showing 500,000.00, and a date
      // filter works on dd/mm/yyyy rather than the raw ISO value.

      // A flagged row carries identity only - no imported/cash_call figures and
      // no action - so every other column reads as empty rather than throwing
      // on a missing object.
      if (row.is_mismatch) {
        switch (field) {
          case 'claim_reference':
          case 'limeiq_key':
          case 'limeiq_reference':
          case 'insured_name':
          case 'coverholder':
            return String(row[field] || '')
          case 'date_of_loss': return this.formatDate(row.date_of_loss)
          default: return ''
        }
      }

      switch (field) {
        case 'date_of_loss': return this.formatDate(row.date_of_loss)
        case 'submitted': return this.formatDate(row.submitted)
        case 'imported_indemnity': return this.money(row.imported.indemnity)
        case 'imported_fees': return this.money(row.imported.fees)
        case 'cash_call_indemnity': return this.money(row.cash_call.indemnity)
        case 'cash_call_fees': return this.money(row.cash_call.fees)
        case 'export_indemnity':
          return this.money(this.derivedFigure(row, 'indemnity', 'export_paid_this_month'))
        case 'export_fees':
          return this.money(this.derivedFigure(row, 'fees', 'export_paid_this_month'))
        case 'totals_indemnity':
          return this.money(this.derivedFigure(row, 'indemnity', 'included_in_totals'))
        case 'totals_fees':
          return this.money(this.derivedFigure(row, 'fees', 'included_in_totals'))
        default:
          return String(row[field] == null ? '' : row[field])
      }
    },
    setColumnFilter(field, value) {
      // Provided to the column heads; assignment is safe because every key is
      // declared up front in data(), so Vue 2 reactivity already tracks it.
      this.columnFilters[field] = value
    },
    clearFilters() {
      Object.keys(this.columnFilters).forEach((key) => {
        this.columnFilters[key] = ''
      })
    },
    exportAmount(value) {
      // Figures go into the sheet as NUMBERS, not the formatted strings shown
      // on screen: "500,000.00" as text cannot be summed or sorted in Excel.
      // A nil derived figure (matrix scenario 6) stays genuinely empty rather
      // than becoming a misleading 0.
      return value == null ? '' : Number(value)
    },
    downloadExcel() {
      // Exports what is on screen: with a filter active the user means the rows
      // they can see, not the ones the filter hid.
      //
      // Two header rows mirror the table's own grouping, so the downloaded file
      // reads the same way as the page it came from.
      const groupRow = [
        '', '', '', '', '', '',
        'Imported Paid This Month', '',
        'LimeIQ Cash Call', '', '',
        '', '',
        'Export Paid This Month', '',
        'Included in Totals', '',
        '',
      ]
      const headerRow = [
        'Claim Ref', 'LimeIQ Key', 'LimeIQ Reference', 'Insured Name',
        'Coverholder', 'Date of Loss',
        'Indemnity', 'Fees',
        'Submitted', 'Indemnity', 'Fees',
        'Mark as Paid?', 'Cash Call Action',
        'Indemnity', 'Fees',
        'Indemnity', 'Fees',
        'Reason',
      ]

      const body = this.visibleRows.map(({ row }) => ([
        row.claim_reference, row.limeiq_key, row.limeiq_reference,
        row.insured_name, row.coverholder,
        this.formatDate(row.date_of_loss),
        this.exportAmount(row.imported.indemnity),
        this.exportAmount(row.imported.fees),
        this.formatDate(row.submitted),
        this.exportAmount(row.cash_call.indemnity),
        this.exportAmount(row.cash_call.fees),
        row.status, row.action,
        this.exportAmount(this.derivedFigure(row, 'indemnity', 'export_paid_this_month')),
        this.exportAmount(this.derivedFigure(row, 'fees', 'export_paid_this_month')),
        this.exportAmount(this.derivedFigure(row, 'indemnity', 'included_in_totals')),
        this.exportAmount(this.derivedFigure(row, 'fees', 'included_in_totals')),
        row.overrides.reason || '',
      ]))

      const sheet = XLSX.utils.aoa_to_sheet([groupRow, headerRow, ...body])

      // Merge each group heading across the columns it spans, matching the
      // table's own two-tier header.
      sheet['!merges'] = [
        { s: { r: 0, c: 6 }, e: { r: 0, c: 7 } },    // Imported Paid This Month
        { s: { r: 0, c: 8 }, e: { r: 0, c: 10 } },   // LimeIQ Cash Call
        { s: { r: 0, c: 13 }, e: { r: 0, c: 14 } },  // Export Paid This Month
        { s: { r: 0, c: 15 }, e: { r: 0, c: 16 } },  // Included in Totals
      ]

      // Column widths, so nothing opens truncated: identity columns are sized
      // for their content and the money columns for a formatted figure.
      sheet['!cols'] = [
        { wch: 12 }, { wch: 46 }, { wch: 22 }, { wch: 24 }, { wch: 18 },
        { wch: 13 },
        { wch: 15 }, { wch: 13 },
        { wch: 13 }, { wch: 15 }, { wch: 13 },
        { wch: 14 }, { wch: 34 },
        { wch: 15 }, { wch: 13 },
        { wch: 15 }, { wch: 13 },
        { wch: 40 },
      ]
      sheet['!freeze'] = { xSplit: 0, ySplit: 2 }

      // Two decimal places with thousands separators on every money column.
      const moneyColumns = [6, 7, 9, 10, 13, 14, 15, 16]
      body.forEach((_, rowIndex) => {
        moneyColumns.forEach((col) => {
          const address = XLSX.utils.encode_cell({ r: rowIndex + 2, c: col })
          const cell = sheet[address]
          if (cell && cell.t === 'n') cell.z = '#,##0.00'
        })
      })

      const workbook = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(workbook, sheet, 'Cash Call Reconciliation')
      // formatDate gives dd/mm/yyyy, and a slash is not legal in a filename.
      const now = new Date()
      const stamp = [
        String(now.getDate()).padStart(2, '0'),
        String(now.getMonth() + 1).padStart(2, '0'),
        now.getFullYear(),
      ].join('-')
      XLSX.writeFile(workbook, `Cash Call Reconciliation - ${stamp}.xlsx`)
    },
    amountInput(index, group, component, raw) {
      // The "Other" fields are text inputs, not type="number": a number input
      // cannot show thousands separators, so a typed 5000000 rendered without
      // commas right beside a formatted 500,000.00 in the row below it.
      //
      // Keep only digits, one decimal point and a leading minus, store the
      // numeric value for the export, and echo the grouped form back to the
      // field. A trailing "." or ".x" is preserved so the user can keep typing.
      const cleaned = String(raw == null ? '' : raw)
        .replace(/[^0-9.-]/g, '')
        .replace(/(?!^)-/g, '')
        .replace(/\.(?=.*\.)/g, '')

      const row = this.rows[index]
      row.overrides[group][component] = cleaned === '' ? 0 : Number(cleaned) || 0

      const [whole, decimals] = cleaned.split('.')
      let display = whole === '' || whole === '-'
        ? whole
        : Number(whole).toLocaleString()
      if (decimals !== undefined) display += `.${decimals.slice(0, 2)}`
      row.display[group][component] = display

      this.recompute(index)
    },
    isAlreadyPaid(row) {
      // Paid on the Cash Call page before this export - marked there directly,
      // or by an earlier export, or (Co-Lead) this UMR is settled. Its status
      // is the Cash Call page's to change, not an export's, so the toggle is
      // locked on Paid.
      return row.originalStatus === 'Paid'
    },
    toggleStatus(index) {
      const row = this.rows[index]
      if (this.isAlreadyPaid(row)) return
      row.status = row.status === 'Paid' ? 'Outstanding' : 'Paid'
      // LIQ-2412: marking an outstanding cash call Paid here defaults to paid
      // in full on the last day of the reporting period; the popup can change
      // either. Toggling back discards it - including a partial or an
      // installment entered in the popup. A cash call that was already Paid
      // before this export records nothing, whichever way it is toggled.
      row.payment = row.status === 'Paid' && row.originalStatus !== 'Paid'
        ? this.defaultPayment(row)
        : null
      this.applyPaymentFigures(row)
      this.onStatusChange(index)
    },
    onStatusChange(index) {
      // Toggling Paid/Outstanding re-runs the suggestion for the row, so the
      // action and both derived figures update dynamically (story AC3).
      const row = this.rows[index]
      this.resuggest(row)
      // The suggestion is never "Other", so a toggle always moves the row off
      // it - and must drop what was typed there, exactly as picking another
      // action from the dropdown does.
      this.clearOtherFigures(row)
      this.emitSelection()
    },
    onActionChange(index) {
      const row = this.rows[index]
      if (row.action !== ACTION_OTHER) this.clearOtherFigures(row)
      this.recompute(index)
    },
    clearOtherFigures(row) {
      // Figures typed under "Other" belong to that action alone. Leaving them
      // behind when the row moves to another action - from the dropdown or the
      // Paid/Outstanding toggle - means coming back to "Other" silently
      // restores numbers the inputs no longer show, and they would be
      // exported. The reason goes too, so the audit trail never records one
      // against an action that took no manual figures.
      row.overrides.export = { indemnity: 0, fees: 0 }
      row.overrides.totals = { indemnity: 0, fees: 0 }
      row.overrides.reason = ''
      row.display.export = { indemnity: '', fees: '' }
      row.display.totals = { indemnity: '', fees: '' }
    },
    recompute(index) {
      // Re-derive both components from the currently selected action. Mirrors
      // the backend's apply_action() so the screen always shows exactly what
      // the export will write.
      const row = this.rows[index]
      const action = row.action
      row.derived = {
        indemnity: {
          action,
          ...this.applyAction(action, row.imported.indemnity, row.cash_call.indemnity,
            row.overrides.export.indemnity, row.overrides.totals.indemnity),
        },
        fees: {
          action,
          ...this.applyAction(action, row.imported.fees, row.cash_call.fees,
            row.overrides.export.fees, row.overrides.totals.fees),
        },
      }
      this.emitSelection()
    },
    suggestComponent(importedAmount, cashCallAmount, isPaid) {
      const imported = Number(importedAmount || 0)
      const cashCall = Number(cashCallAmount || 0)
      if (!isPaid) {
        return { action: ACTION_NO_ACTION, export_paid_this_month: imported, included_in_totals: imported }
      }
      if (Math.abs(imported) < MATCH_TOLERANCE) {
        return { action: ACTION_CASH_CALL_INCLUDED, export_paid_this_month: cashCall, included_in_totals: 0 }
      }
      if (Math.abs(imported - cashCall) < MATCH_TOLERANCE) {
        return { action: ACTION_MATCHED, export_paid_this_month: cashCall, included_in_totals: 0 }
      }
      if (imported < cashCall) {
        return {
          action: ACTION_CASH_CALL_AND_ADDITIONAL,
          export_paid_this_month: cashCall + imported,
          included_in_totals: imported,
        }
      }
      return {
        action: ACTION_MATCHED_AND_ADDITIONAL,
        export_paid_this_month: imported,
        included_in_totals: imported - cashCall,
      }
    },
    applyAction(action, importedAmount, cashCallAmount, overrideExport, overrideTotals) {
      const imported = Number(importedAmount || 0)
      const cashCall = Number(cashCallAmount || 0)
      switch (action) {
        case ACTION_OTHER:
          return {
            export_paid_this_month: Number(overrideExport || 0),
            included_in_totals: Number(overrideTotals || 0),
          }
        case ACTION_NO_ACTION:
          return { export_paid_this_month: imported, included_in_totals: imported }
        case ACTION_CASH_CALL_INCLUDED:
        case ACTION_MATCHED:
          return { export_paid_this_month: cashCall, included_in_totals: 0 }
        case ACTION_CASH_CALL_AND_ADDITIONAL:
          return { export_paid_this_month: cashCall + imported, included_in_totals: imported }
        case ACTION_MATCHED_AND_ADDITIONAL:
          return {
            export_paid_this_month: imported,
            included_in_totals: Math.max(imported - cashCall, 0),
          }
        default:
          return { export_paid_this_month: imported, included_in_totals: imported }
      }
    },
    emitSelection() {
      // Hand the confirmed reconciliation up to the wizard, which sends it with
      // the export so the netting is applied and the Paid/Outstanding toggles
      // are written back to the Cash Call page.
      this.$emit('input', {
        // Flagged rows are excluded: they carry no figures and no action, so
        // there is nothing to confirm. Leaving them out here means a key
        // mismatch can never reach the write-back or the netting at all - the
        // backend guards against it too, but the row should never arrive.
        rows: this.rows.filter((row) => !row.is_mismatch).map((row) => ({
          cash_call_id: row.cash_call_id,
          cash_call_key_id: row.cash_call_key_id,
          claim_reference: row.claim_reference,
          limeiq_key: row.limeiq_key,
          status: row.status,
          // What the scan said, so a return to this step can tell the user's
          // own toggle from a status that only repeated an earlier scan.
          scanned_status: row.originalStatus,
          action: row.action,
          suggested_action: row.suggestedAction,
          // Flagged for the backend audit trail when the user moved away from
          // the auto-suggestion, or entered figures manually.
          is_override: row.action !== row.suggestedAction,
          reason: row.overrides.reason || '',
          // The figure the BDX imported for this row, sent so the backend can
          // fold several cash calls onto one exported row without counting the
          // imported amount once per cash call.
          imported: {
            indemnity: row.imported.indemnity,
            fees: row.imported.fees,
          },
          // What the CASH CALL itself settled, plus the share slice this key
          // represents. The write-back stores these on the Cash Call page -
          // it must not store "export paid this month", which is a row figure
          // carrying the TPA's own imported amount as well.
          cash_call: {
            indemnity: row.cash_call.indemnity,
            fees: row.cash_call.fees,
          },
          share_fraction: row.share_fraction,
          // LIQ-2412: the payment the export records on the Cash Call.
          payment: row.payment || null,
          export_paid_this_month: {
            indemnity: row.derived.indemnity.export_paid_this_month,
            fees: row.derived.fees.export_paid_this_month,
          },
          included_in_totals: {
            indemnity: row.derived.indemnity.included_in_totals,
            fees: row.derived.fees.included_in_totals,
          },
          // LIQ-2474 — which reporting month this confirmation belongs to.
          // Empty for a single-period export. The backend writes the figure
          // onto that month only when several periods were exported together.
          reporting_period: row.reporting_period || '',
        })),
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.cash-call-reconciliation {
  // The sub-heading sits BELOW the title, so the gap before the table belongs
  // to the eyebrow and the title keeps only the small gap to its sub-heading.
  &__title {
    font-weight: 700;
    margin-bottom: 2px;
  }
  &__eyebrow {
    font-size: 0.78rem;
    color: #6b7280;
    margin-bottom: 18px;
  }
  &__spinner { color: #14433a; }

  &__empty {
    padding: 58px 22px;
    text-align: center;

    h3 {
      margin: 0 0 6px;
      font-size: 0.94rem;
      font-weight: 700;
      color: #1f2937;
    }

    p {
      margin: 0 auto;
      max-width: 430px;
      font-size: 0.82rem;
      line-height: 1.5;
      color: #6b7280;
    }
  }

  &__empty-check {
    width: 42px;
    height: 42px;
    margin: 0 auto 13px;
    border-radius: 50%;
    background: #eaf2f0;
    border: 1px solid #14433a;
    color: #14433a;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    font-weight: 700;
  }

  // The percentage budget below fits one screen by default (story 4.2). Once a
  // user widens a column past that, the wrapper scrolls rather than squeezing
  // the rest - the default view is still the unscrolled one.
  &__toolbar {
    display: flex;
    align-items: center;
    // Several named filters can be active at once, so the row wraps rather
    // than squeezing the chips or pushing Download off the edge.
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 10px;

    // Matches the wizard's own Next button, and holds that colour in EVERY
    // state. Bootstrap repaints .btn on :focus and :active, which is what
    // turned this button pale blue once it had been clicked - a button that
    // changes colour on click reads as disabled rather than pressed.
    .cc-tool-btn {
      white-space: nowrap;
      background-color: #1c4a50;
      border-color: #1c4a50;
      color: #fff;

      &:hover,
      &:focus,
      &:active,
      &:not(:disabled):not(.disabled):active,
      &:not(:disabled):not(.disabled).active {
        background-color: #16393e;
        border-color: #16393e;
        color: #fff;
        box-shadow: none;
      }

      &:focus-visible {
        box-shadow: 0 0 0 2px rgba(28, 74, 80, 0.35);
      }

      // Genuinely unavailable (nothing to download) - the one state where a
      // muted treatment is correct.
      &:disabled,
      &.disabled {
        background-color: #9fb3b5;
        border-color: #9fb3b5;
        color: #fff;
        opacity: 1;
      }
    }
    .cc-clear-btn {
      padding: 0 4px;
      color: #14433a;
      text-decoration: underline;
    }
    .cc-count {
      font-size: 0.78rem;
      color: #6b7280;
    }

    // One chip per active filter: the column name, the value it is matching,
    // and an x to lift just that one.
    .cc-filter-chip {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      max-width: 260px;
      padding: 2px 8px;
      border: 1px solid #cfdad7;
      border-radius: 11px;
      background: #f2f6f5;
      font-size: 0.72rem;
      line-height: 1.5;
      color: #14433a;
      cursor: pointer;

      &__label { color: #5b6b67; }

      // The value is the part that can be arbitrarily long, so it is the part
      // that truncates - the column name stays whole.
      &__value {
        font-weight: 600;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      &__x {
        font-size: 0.95rem;
        line-height: 1;
        color: #6b7280;
      }

      &:hover {
        background: #e6eeec;
        border-color: #a8bdb8;
        .cc-filter-chip__x { color: #14433a; }
      }

      &:focus { outline: none; }
      &:focus-visible {
        outline: none;
        box-shadow: 0 0 0 2px rgba(78, 125, 120, 0.3);
      }
    }
  }

  // A column heading and its own search control.
  //
  // Every heading is a single line with its icon pinned to the right. Letting
  // the label wrap is what produced the stranded and orphaned icons: the icon
  // is an inline item, so it lands wherever the line happens to break. A flex
  // row with a non-wrapping, ellipsised label keeps the two locked together
  // and on one level across all 17 columns.
  .cc-head {
    display: flex;
    // Centre, not baseline: the icon is an SVG box with no text baseline of
    // its own, so baseline alignment drops it below the heading text.
    align-items: center;
    gap: 5px;
    width: 100%;

    // The icon is always last in the markup, so it is always on the right.
    // Right-aligned money columns only push the pair over to the end.
    &--right { justify-content: flex-end; }

    &__label {
      // No ellipsis, no shrinking: a column name must read in full. The table
      // carries a px min-width budgeted from these labels (see __table), so
      // the room is guaranteed rather than competed for.
      flex: 0 0 auto;
      white-space: nowrap;
    }

    &__icon {
      // Always visible: a filter the reader cannot see is a filter they will
      // not use. Hiding it until hover saved ~255px of width but left every
      // heading looking unfinished, which is not a trade worth making.
      flex: 0 0 auto;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      border: 0;
      // Held across every state: a bare `background: none` is repainted by
      // Bootstrap's button rules on :active / :focus, which filled a dark box
      // behind the icon the moment the popover opened.
      background: transparent;
      padding: 0;
      line-height: 0;
      color: #9aa5a1;
      cursor: pointer;
      -webkit-tap-highlight-color: transparent;

      &:active,
      &:focus,
      &:hover {
        background: transparent;
        box-shadow: none;
      }
      // The SVG takes its stroke from currentColor, so only the colour needs
      // to change between states.
      svg { display: block; flex: 0 0 auto; }

      &:hover { color: #14433a; }

      // Solid while that column is filtering, so an applied filter is visible
      // without opening the popover.
      &.is-active { color: #14433a; }
    }

  }

  // Opening the popover moves focus to this button, and the browser treats
  // that programmatic move as keyboard-like - so a plain focus ring drew a
  // dark box around the icon on every click. Suppressed, with a soft halo
  // kept for real keyboard use (the same treatment as .cc-switch below).
  // NOTE: the focus treatment for this button lives in the UNSCOPED block at
  // the end of the file - Bootstrap's own button focus styles are global, and
  // a scoped rule here loses to them.

  .cc-no-match {
    padding: 26px 0 8px;
    text-align: center;
    color: #6b7280;
    font-size: 0.85rem;
    margin: 0;
  }

  // The 17 headings need ~1700px to render in full with their filter icons
  // and the side padding that separates one heading from the next.
  // Above that the percentage budget fits the wrapper and nothing scrolls;
  // below it the table holds its min-width and the wrapper scrolls instead of
  // clipping column names, which must always read in full.
  &__table-wrap {
    width: 100%;
    max-width: 100%;
    overflow-x: auto;
  }


  &__table {
    width: 100%;
    // Budgeted from the widest heading in each column plus its 11px icon and
    // padding. Without a px floor the percentage widths just divide whatever
    // space exists, and every long name ("LimeIQ Reference", "Cash Call
    // Action") is cut off on a narrower viewport.
    //
    // Side padding is deliberately tight throughout: it is paid 17 times over,
    // so every pixel of it costs 34px of table width and therefore of scroll.
    // The percentages above are each column's share of that measured need,
    // not round numbers - so no column carries width its content does not use.
    min-width: 1700px;
    table-layout: fixed;
    border-collapse: collapse;
    font-size: 0.72rem;

    th, td {
      padding: 4px 6px;
      vertical-align: middle;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    // A heading ends with its filter icon, so it needs enough side padding
    // that the icon does not butt into the next column's name. 8px is the
    // floor for that: across 17 columns every extra pixel here costs 34px of
    // table width, which is what makes this table scroll at all. The hairline
    // rule below does the separating work that wider padding would.
    thead th { padding: 6px 8px 5px; }

    // Row separators belong to the DATA only. Putting them on every cell also
    // ruled the group header row, drawing a line under each band label and a
    // stray one over the identity columns (which sit under an empty spacer).
    tbody td { border-bottom: 1px solid #eceff1; }

    // Every heading is one line, all on the same level, and never clipped -
    // `overflow: visible` so a name is shown in full even if its column is
    // momentarily narrower than the label (the table min-width makes that the
    // exception rather than the rule).
    thead th {
      background: #f7f9f8;
      font-weight: 700;
      color: #3c4043;
      white-space: nowrap;
      overflow: visible;
      text-overflow: clip;
      line-height: 1.25;
      vertical-align: middle;
    }

    // Padding alone leaves the boundary ambiguous once a heading ends in an
    // icon: a hairline rule says where one column stops and the next starts.
    // Not on the first of each band - the band already has its own separator.
    .cash-call-reconciliation__sub-row th + th:not(.l) {
      border-left: 1px solid #e8ecea;
    }

    // Only the LAST header row closes against the data. The group row above it
    // merely labels the bands, so giving every thead cell a bottom border drew
    // a rule under each group label and, worse, a stray one over the identity
    // columns - which sit under an empty spacer cell with no label at all.
    thead tr:last-child th { border-bottom: 1px solid #dfe3e1; }

    // The two controls span both header rows, so they close against the data
    // themselves rather than via the last row.
    thead th[rowspan] { border-bottom: 1px solid #dfe3e1; }

    // A vertical rule opens each column band - the two money groups, the two
    // controls, and the two derived groups - so a figure is always read against
    // the right heading. The band's right edge is closed by the next rule.
    .l { border-left: 1px solid #dfe3e1; }

    .group { text-align: center; border-left: 1px solid #dfe3e1; }
    .mid { text-align: center; }
    .num { text-align: right; }
    .ident { color: #3c4043; }
    // Identity values are abbreviated on one line; the full value is on the
    // cell's title attribute, shown on hover.
    .key {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    // Dates and figures are never abbreviated - a truncated amount is
    // unreadable and a half-shown date is ambiguous. `visible` suits plain
    // text, but a cell holding an "Other" input must clip it to the column, so
    // those revert to hidden below.
    td.num, td.date {
      overflow: visible;
      text-overflow: clip;
      white-space: nowrap;
    }

    td.num.has-input { overflow: hidden; }

    // Values the user reconciles against: shown whole, never ellipsised.
    .whole {
      overflow: visible;
      text-overflow: clip;
    }

    // Both figures in a pair are right-aligned, so a six-figure Indemnity can
    // run right up to its Fees neighbour ("500,000.00 996.00"). Padding the
    // left of each Indemnity and the right of each Fees keeps the two readable
    // as separate amounts.
    td.num, th.num { padding-right: 7px; }
    // Any cell opening a group needs clearance from that group's rule, not
    // just the numeric ones - Submitted opens the LimeIQ Cash Call group and
    // would otherwise sit hard against the border.
    td.l, th.l { padding-left: 7px; }
    // Submitted sits immediately left of a right-aligned figure, so it needs
    // room on both sides to read as its own column.
    td.date.l, th.l:not(.num) { padding-right: 8px; }

    // Extra separation where two identity headings would otherwise read as one
    // phrase ("LimeIQ Reference Insured Name") - modest now that a hairline
    // rule separates every heading anyway.
    .gap-after { padding-right: 10px; }

    // A cash call actioned on THIS page renders bold, so it reads apart from
    // one that was already Paid on the Cash Call page before this export ran.
    // Applied to the cells rather than the row: <tr> font-weight does not
    // inherit into the toggle label or the <select>.
    tr.is-actioned-here > td { font-weight: 700; }
    tr.is-actioned-here .cc-switch__text { font-weight: 700; }
    tr.is-actioned-here select { font-weight: 700; }

    // Flagged rows (claim reference present under a different LimeIQ key).
    // Greyed so they read as informational next to the actionable rows, but
    // still legible - the identity values are exactly what the TPA needs in
    // order to report the problem.
    tr.is-mismatch > td {
      background: #f7f8f8;
      color: #8a9299;
    }

    tr.is-mismatch .mismatch-note {
      font-style: italic;
      white-space: normal;
    }

    tr.is-mismatch .mismatch-note a {
      color: #5f6b73;
      text-decoration: underline;
    }

    // "Date of Loss" wraps onto two lines, so its second word sits level with
    // the Coverholder heading and the two read as one phrase. Pulling
    // Coverholder left within its own column separates them without moving any
    // column boundary.
    th.coverholder-col, td.coverholder-col {
      padding-left: 2px;
      padding-right: 22px;
    }

    // "Cash Call Action" is kept on one line without widening the column: the
    // label is set a little smaller and tighter so it fits the 8% budget, and
    // the (i) trigger sits beside it in a centred row rather than inline,
    // where it was pushed out onto the column rule.
    .nowrap-head {
      // This is the only heading carrying THREE things - label, help icon and
      // filter icon. The right side needs clearance so the icons do not sit on
      // the column rule; the left opens a band and already has .l's padding,
      // so it stays tight.
      padding-left: 6px;
      padding-right: 10px;

      .head-inner {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 4px;
        flex-wrap: nowrap;
        white-space: nowrap;
        // Inherits the table's heading size. It was shrunk to 0.55rem back when
        // this column was narrower than its neighbour; now that the two controls
        // are budgeted the same width, a smaller font just made the pair look
        // mismatched.
        letter-spacing: 0;

        .info-icon {
          width: 11px;
          height: 11px;
          line-height: 11px;
          font-size: 8px;
        }
      }
    }

    tbody tr:hover { background: #fafbfb; }
  }

  &__group-row th { font-size: 0.68rem; text-transform: uppercase; letter-spacing: 0.02em; }
  &__sub-row th { font-size: 0.66rem; font-weight: 600; }

  .info-icon {
    display: inline-block;
    width: 13px; height: 13px;
    line-height: 13px;
    border-radius: 50%;
    background: #9aa0a6;
    color: #fff;
    font-size: 9px;
    font-style: italic;
    text-align: center;
    flex: 0 0 auto;
    cursor: help;
  }

  // Matched is the ONLY coloured figure (story 4.4).
  .matched { color: #1e7e45; font-weight: 600; }

  // b-form-select renders the native <select> itself carrying .custom-select,
  // so this element IS the select - not a wrapper around one.
  //
  // The action labels are long ("LimeIQ Cash Call & additional included in
  // export"), and .custom-select-sm sets only padding-left, leaving the text to
  // run under the chevron. Reserve room for it on the right explicitly.
  .action-select {
    font-size: 0.66rem;
    width: 100%;
    padding-left: 7px;
    padding-right: 20px !important;   // beats .custom-select's 1.75rem
    text-overflow: ellipsis;
    background-position: right 6px center;
  }
  // Matched is the only action given a colour (story 4.4). The class sits on
  // the select itself, so it is styled directly rather than via a descendant.
  .action-select--matched { color: #1e7e45; font-weight: 600; }
  // The "Other" inputs must sit INSIDE their own money column. A fixed pixel
  // width overflowed the ~4.5% columns and pushed the whole row sideways, so
  // each input fills its cell instead.
  .num-input {
    width: 100%;
    min-width: 0;
    font-size: 0.66rem;
    text-align: right;
    padding: 2px 3px;
    height: 22px;

    // A number input reserves ~16px for its spinner arrows, which on a column
    // this narrow is the difference between reading "500" and "500000". These
    // are amounts typed in full, not stepped, so the spinners are removed.
    -moz-appearance: textfield;

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
  }

  // The Paid/Outstanding switch and its label sit on one line, aligned
  // consistently down the column. BootstrapVue renders the switch as an
  // inline-block with its own padding and min-height, which both misaligns it
  // per row and lets it overhang into the neighbouring Fees column - so the
  // cell reserves its own width and the control is laid out inside it.
  // Mark as Paid? - the switch must sit inside this column, under its own
  // header, and never bleed into Fees on its left.
  //
  // BootstrapVue draws the switch track/knob as ::before/::after on the label,
  // positioned in the control's own `padding-left` gutter. `overflow: hidden`
  // on the cell therefore CLIPS the knob rather than containing it, and the
  // default gutter starts at the cell's left edge - which is what pushed the
  // control under the Fees column. So the gutter is replaced outright: the
  // control becomes a flex row that starts after the cell's own padding, and
  // the track/knob are positioned relative to that instead.
  td.mark-col, th.mark-col {
    text-align: left;
    padding-left: 10px;
    padding-right: 4px;
    white-space: nowrap;
    // The switch draws its own track/knob and the state word must show in
    // full, so this cell does not clip its contents. The column is budgeted
    // to hold "OUTSTANDING" beside the track (~124px of content in ~134px of
    // column), so nothing has to escape the cell to stay readable.
    overflow: visible;
  }

  // Self-contained pill switch. Every dimension is declared here, so none of
  // the app's global custom-control rules apply.
  .cc-switch {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 0;
    border: 0;
    background: none;
    cursor: pointer;
    max-width: 100%;

    &__track {
      position: relative;
      flex: 0 0 auto;
      width: 34px;
      height: 17px;
      border-radius: 17px;          // fully rounded pill
      background: #e2e6e4;
      transition: background-color 0.15s ease-in-out;
    }

    &__knob {
      position: absolute;
      top: 2px;
      left: 2px;
      width: 13px;
      height: 13px;
      border-radius: 50%;           // circular knob
      background: #fff;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
      transition: transform 0.15s ease-in-out;
    }

    // Written out rather than nested as `&--on &__track`: that form compiles to
    // ".cc-switch--on .cc-switch__track" as a DESCENDANT of .cc-switch, which
    // never matches, because --on sits on the same element.
    &.cc-switch--on .cc-switch__track { background: #4e7d78; }
    &.cc-switch--on .cc-switch__knob { transform: translateX(17px); }

    // "OUTSTANDING" is the longest state and must never be abbreviated - it is
    // the state that keeps a cash call out of the export, so no ellipsis here.
    &__text {
      font-size: 0.6rem;
      font-weight: 700;
      letter-spacing: 0.01em;
      text-transform: uppercase;
      color: #3c4043;
      white-space: nowrap;
      overflow: visible;
      text-overflow: clip;
    }

    // Outstanding keeps money out of the export, so it reads at a glance.
    &:not(.cc-switch--on) .cc-switch__text { color: #c1720f; }

    &:focus { outline: none; }
    &:focus-visible .cc-switch__track { box-shadow: 0 0 0 2px rgba(78, 125, 120, 0.3); }

    // Already Paid on the Cash Call page: shown, but greyed and not clickable.
    &.cc-switch--locked {
      cursor: not-allowed;
      opacity: 0.55;

      .cc-switch__track { background: #a9b3b1; }
      .cc-switch__text { color: #6b7280; }
    }
  }

  // LIQ-2412: the payment line under the switch - what will be recorded and
  // when. Small enough to sit in the switch's column without widening it.
  .cc-paid-meta {
    display: flex;
    align-items: baseline;
    gap: 4px;
    margin-top: 3px;
    font-size: 0.6rem;
    line-height: 1.2;
    color: #4b5563;

    &__text {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    &__link {
      flex: 0 0 auto;
      padding: 0;
      border: 0;
      background: none;
      font-size: inherit;
      color: #14433a;
      text-decoration: underline;
      cursor: pointer;

      &:focus { outline: none; }
    }
  }

  .reason-row td {
    background: #fbfbfa;
    border-bottom: 1px solid #eceff1;
  }
  .reason-label {
    font-size: 0.66rem;
    font-weight: 600;
    color: #6b7280;
    margin-right: 8px;
    margin-bottom: 0;
  }
  .reason-input { display: inline-block; width: 60%; font-size: 0.7rem; height: 26px; }
}
</style>

<style lang="scss">
// The filter icon's focus treatment. Unscoped deliberately: Bootstrap styles
// buttons globally, and opening the popover moves focus to this one - a
// scoped rule loses that specificity contest and the browser's default ring
// draws a dark box around the icon on every click.
.cc-head__icon,
.cc-head__icon:hover,
.cc-head__icon:active,
.cc-head__icon:focus {
  outline: none !important;
  box-shadow: none !important;
  background: transparent !important;
  border: 0;
}
// A soft halo for genuine keyboard use only. After the reset above, since
// :focus-visible is also :focus and order decides.
.cc-head__icon:focus-visible {
  outline: none !important;
  border-radius: 3px;
  box-shadow: 0 0 0 2px rgba(78, 125, 120, 0.3) !important;
}

// Drag handle for column resizing. Unscoped because the handles are created
// with document.createElement, so they carry no scope attribute.
// Unscoped: BootstrapVue renders the popover at the end of <body>, outside this
// component's root, so a scoped attribute selector would never reach it.
//
// A fixed, readable card rather than a narrow tooltip: six explanations in a
// tooltip's default width stacked into a column taller than the viewport.
// Per-column filter popover. Small and plain - it holds one control, so it
// should feel like part of the header rather than a dialog.
.popover.cc-filter-pop {
  max-width: 250px;
  border: 1px solid #e3e8e6;
  box-shadow: 0 6px 18px rgba(16, 32, 28, 0.12);

  .popover-body { padding: 8px; }

  .cc-filter-pop__body {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .cc-filter-pop__input {
    flex: 1 1 auto;
    min-width: 0;
    font-size: 0.78rem;
    padding: 4px 7px;
    border: 1px solid #d5dad8;
    border-radius: 3px;
    background: #fff;
    color: #1f2937;

    &:focus {
      outline: none;
      border-color: #4e7d78;
    }
  }

  .cc-filter-pop__clear {
    flex: 0 0 auto;
    border: 0;
    background: none;
    padding: 0 2px;
    font-size: 0.72rem;
    color: #14433a;
    text-decoration: underline;
    cursor: pointer;
  }
}

.popover.cc-action-help {
  max-width: 560px;
  width: 560px;
  border: 1px solid #e3e8e6;
  border-radius: 6px;
  box-shadow: 0 6px 24px rgba(11, 11, 11, 0.12);
  background: #fff;

  // Six explanations run taller than the space either above or below the
  // header, so the card is capped to the viewport and scrolls its own body
  // rather than running off-screen and hiding the last options.
  .popover-body {
    max-height: 60vh;
    overflow-y: auto;
  }

  .popover-header {
    background: #fff;
    border-bottom: 1px solid #eceff1;
    font-size: 0.86rem;
    font-weight: 700;
    color: #1f2937;
    padding: 12px 16px;
    border-radius: 6px 6px 0 0;

    // The default caret sits awkwardly against a white header.
    &::before { border-bottom: none; }
  }

  .popover-body { padding: 0; }

  .cc-action-help__item {
    padding: 11px 16px;
    border-bottom: 1px solid #eceff1;

    &:last-child { border-bottom: none; }
  }

  .cc-action-help__label {
    font-size: 0.8rem;
    font-weight: 700;
    color: #1f4d47;
    margin-bottom: 3px;
  }

  .cc-action-help__text {
    font-size: 0.78rem;
    line-height: 1.45;
    color: #4b5563;
  }
}

// LIQ-2412 Mark as Paid popup. Unscoped: BootstrapVue renders the modal at the
// end of <body>. The look follows the Cash Call page's Mark as Paid, whose own
// styles live in that page's bundle and are not loaded in the wizard.
.cc-pay-modal {
  max-width: 680px;

  .cc-pay-summary {
    background: #eef6f1;
    border-radius: 6px;
    padding: 12px 14px;
    margin-bottom: 14px;
    color: #333;
  }
  .cc-pay-money { color: #2e7d32; font-weight: 600; margin-left: 4px; }
  .cc-pay-breakdown {
    margin-top: 6px;
    display: flex;
    gap: 20px;
    font-size: 13px;
    color: #2e7d32;

    label { color: #333; margin: 0 2px 0 0; }
  }

  .cc-pay-history {
    margin-bottom: 14px;
    font-size: 13px;

    > label { display: block; color: #333; margin-bottom: 4px; }
    table { width: 100%; }
    td { padding: 2px 6px 2px 0; }
    .cc-pay-history__amt { text-align: right; white-space: nowrap; }
    .cc-pay-history__cmt { color: #6b7280; }
  }

  .cc-pay-field {
    margin-bottom: 14px;

    > label {
      display: block;
      font-size: 16px;
      margin-bottom: 6px;
      color: #333;
    }
  }

  .cc-pay-select { width: 150px; }
  .cc-pay-input,
  .cc-pay-textarea {
    border: 1px solid #ced4da;
    border-radius: 4px;
    padding: 8px 10px;
    font-size: 14px;
    background: #fff;
  }
  .cc-pay-input { width: 150px; height: 38px; }
  .cc-pay-textarea { width: 100%; min-height: 65px; resize: vertical; }

  .cc-pay-fee-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
    gap: 12px;
  }
  .cc-pay-fee-item { display: flex; flex-direction: column; }
  .cc-pay-sub-label { font-size: 13px; font-weight: 500; color: #555; }

  .cc-pay-total {
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 13px;
    font-weight: 600;
    color: #2e7d32;
    margin: -4px 0 14px;

    strong { color: #333; font-weight: normal; }
  }

  .cc-pay-dates { display: flex; gap: 24px; flex-wrap: wrap; }
  .cc-pay-datepicker { width: 160px; }

  .cc-pay-error { color: #dc3545; font-size: 13px; margin: 0 0 6px; }
  .cc-pay-note { color: #6b7280; font-size: 12px; margin: 0; }
}
</style>
